import React from "react";

import * as yup from "yup";
import { Box, Button, Drawer, TextField } from "@mui/material";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { SimpleHead } from "components/Simple-head/SimpleHead";
import { useFormik } from "formik";
import { IApiServiceError, useAccountSelector, useDealActions } from "store";
import { IInvestor } from "store/types";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  saveNewInvestorToSelected: (value: IInvestor) => void;
}
interface IFormikValues {
  name: string;
  group: string;
  email: string;
  phone_number: string;
  company: string;
  additional_information: string;
}

export const CreateNewInvestorDrawer: React.FC<IProps> = ({ isOpen, onClose, saveNewInvestorToSelected }) => {
  const initialValues: IFormikValues = {
    name: "",
    group: "",
    email: "",
    phone_number: "",
    company: "",
    additional_information: ""
  };
  const { createInvestorAction } = useDealActions();
  const { allow_split_by_group } = useAccountSelector() || {};

  const { handleSubmit, values, isValid, setFieldValue, resetForm, errors, setErrors } = useFormik({
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      name: yup.string().nullable().required("This field is required"),
      email: yup.string().email()
    }),
    onSubmit: async () => {
      if (!values.name) return;
      try {
        const response = await createInvestorAction({ ...values });
        saveNewInvestorToSelected(response);
        resetForm();
        onClose();
      } catch (error) {
        setErrors((error as IApiServiceError).errors);
      }
    }
  });

  return (
    <Drawer
      open={isOpen}
      onClose={() => onClose()}
      sx={{
        zIndex: "1310",
        ".MuiPaper-root": {
          width: "430px",
          borderTopRightRadius: "25px",
          borderBottomRightRadius: "25px",
          display: "flex",
          justifyContent: "space-between"
        }
      }}
    >
      <SimpleHead onIconClick={() => onClose()}>Create New Investor</SimpleHead>
      <Box sx={{ height: "100%", padding: "30px", display: "flex", flexDirection: "column", gap: "32px" }}>
        <div>
          <TextField
            helperText={errors.name}
            error={!!errors.name}
            value={values.name || ""}
            required
            fullWidth
            label="Investor Name"
            onChange={(e) => setFieldValue("name", e.target.value)}
          />
        </div>

        {allow_split_by_group && (
          <TextField
            value={values.group || ""}
            fullWidth
            label="Investor Group"
            onChange={(e) => setFieldValue("group", e.target.value)}
          />
        )}
        <TextField
          value={values.email || ""}
          fullWidth
          label="Email Address"
          onChange={(e) => setFieldValue("email", e.target.value)}
          helperText={errors.email || ""}
          error={!!errors.email}
          sx={{
            "& .MuiFormHelperText-root": {
              bottom: "-13px",
              position: "absolute",
              margin: "0",
              color: "#d32f2f",
              width: "400px"
            }
          }}
        />
        <NumberInput
          value={values.phone_number || ""}
          fullWidth
          label="Phone Number"
          onChange={(e) => setFieldValue("phone_number", e.target.value)}
        />
        <TextField
          value={values.company || ""}
          fullWidth
          label="Company"
          onChange={(e) => setFieldValue("company", e.target.value)}
        />
        <TextField
          value={values.additional_information || ""}
          fullWidth
          label="Additional Information"
          onChange={(e) => setFieldValue("additional_information", e.target.value)}
        />
      </Box>
      <Box
        sx={{
          height: "87px",
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          padding: "30px",
          boxShadow: "0 -4px 13px 0 rgba(10, 49, 66, 0.2)",
          textDecoration: "underline"
        }}
      >
        <p
          onClick={() => {
            resetForm();
          }}
        >
          Clear All
        </p>
        <div>
          <Button
            sx={{ marginRight: "21px" }}
            onClick={() => {
              onClose();
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={!isValid}
            onClick={() => {
              handleSubmit();
            }}
            variant="contained"
          >
            Create
          </Button>
        </div>
      </Box>
    </Drawer>
  );
};
